import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  Optional,
  Inject
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { DateService } from '../../../services/date/date.service';
import { DateFormatterService } from '../../../services/date/date-formatter.service';
import { FocusDirective } from '../../focus/focus.directive';
import { DatePickerInterface } from '../date-picker/date-picker-interface';

import type {
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import type { DatePickerFormatType } from '../../../services/date/date-formatter.service';
import type { DateValidator } from '../date-picker/date-validator.type';
import type { DateComparator } from '../date-picker/date-comparator.type';
import type { DateObject } from './date-picker-inner.domain';

type ComparatorType = 'day' | 'month' | 'year' | 'dual-month-day';

interface StepType {
  years?: number,
  months?: number
}

/**
 * The Date Picker Inner Component
 *
 * This is the component that contains the logic for date picker
 */
@Component({
  selector: 'td-wb-date-picker-inner',
  templateUrl: './date-picker-inner.component.html',
  styleUrls: ['./date-picker-inner.component.scss'],
  imports: [FocusDirective, AsyncPipe]
})
export class DatePickerInnerComponent implements OnInit, OnChanges {
  /**
   * Prefix for datepicker id
   */
  private static readonly DATE_PICKER_ID_PREFIX: string = 'datepicker--';

  /**
   * Unique id set for each datepicker
   */
  @Input() id: string;

  /**
   * Starting day of the week from 0-6 (0=Sunday, ..., 6=Saturday)
   */
  @Input() startingDay: number;

  /**
   * Number of years to be displayed in the year selection
   */
  @Input() yearRange: number;

  /**
   * Sets datepicker mode, supports: day, month, year, dual-month-day
   */
  @Input() mode: string;

  /**
   * Sets minimum datepicker mode, supports: day, month, year
   */
  @Input() minMode: string;

  /**
   * Sets maximum datepicker mode, supports: day, month, year
   */
  @Input() maxMode: string;

  /**
   * if false week numbers will be hidden
   */
  @Input() showWeeks: boolean;

  /**
   * Format to parse date entered by user
   */
  @Input() format: string;

  /**
   * Format of day in month
   */
  @Input() formatDay: DatePickerFormatType;

  /**
   * Format of month in year.
   */
  @Input() formatMonth: DatePickerFormatType;

  /**
   * Format of year in year range.
   */
  @Input() formatYear: DatePickerFormatType;

  /**
   * Format of day in week header.
   */
  @Input() formatDayHeader: DatePickerFormatType;

  /**
   * Format of title when selecting day.
   */
  @Input() formatDayTitle: DatePickerFormatType;

  /**
   * Format of title when selecting month.
   */
  @Input() formatMonthTitle: DatePickerFormatType;

  /**
   * Function which should return true if date in the selector should be disabled for selection.
   */
  @Input() disabledDates: DateValidator;

  /**
   * If true only dates of current month will be displayed.
   */
  @Input() displayDatesOfCurrentMontyOnly: boolean;

  /**
   * Event emitter for notifying date has been selected.
   */
  @Output() selectionDone: EventEmitter<Date> = new EventEmitter<Date>();

  /**
   * Date selected from input
   */
  @Input() parsedDate: Date;

  /**
   * Latest selectable date
   */
  @Input() maxDate: Date;

  /**
   * Oldest selectable date
   */
  @Input() minDate: Date;
  /**
   * Event Emitter for focusing calendar
   */
  calendarFocusEventEmitter: EventEmitter<boolean> = new EventEmitter();
  /**
   * Step to move
   */
  step: Map<string, StepType> = new Map<string, StepType>();

  /**
   * Unique id set for each date picker
   */
  uniqueId: string;

  /**
   * Active date
   */
  activeDate: Date;

  /**
   * Allowed modes
   */
  modes: Array<string> = ['day', 'month', 'year', 'dual-month-day'];

  /**
   * Selected date
   */
  selectedDate: Date;

  /**
   * Active date id
   */
  activeDateId: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Function for displaying day view of date picker
   */
  refreshViewHandlerDay: () => void;

  /**
   * Function for comparing day
   */
  compareHandlerDay: DateComparator;

  /**
   * Function for displaying month view of date picker
   */
  refreshViewHandlerMonth: () => void;

  /**
   * Function for comparing month
   */
  compareHandlerMonth: DateComparator;

  /**
   * Function for displaying year view of date picker
   */
  refreshViewHandlerYear: () => void;

  /**
   * Function for comparing year
   */
  compareHandlerYear: DateComparator;

  /**
   * Function for displaying day view of dual month date picker
   */
  refreshViewHandlerDualMonthDay: () => void;

  /**
   * Date picker component
   */
  datePicker: DatePickerInterface;

  /**
   * The constructor of the component.
   *
   * @param dateFormatterService The date service
   * @param datePicker
   * @param hostElement
   */
  constructor(@Inject(DateFormatterService) private dateFormatterService: DateFormatterService,
              @Inject(DatePickerInterface) @Optional() datePicker: DatePickerInterface,
              @Inject(ElementRef) public hostElement: ElementRef) {
    this.datePicker = datePicker;
  }

  /**
   * This function sets unique id and active & selected date
   */
  ngOnInit(): void {
    this.uniqueId = DatePickerInnerComponent.DATE_PICKER_ID_PREFIX + this.hostElement.nativeElement.getAttribute('id');
    if (DateService.isDate(this.parsedDate)) {
      this.activeDate = this.parsedDate;
      this.selectedDate = new Date(this.activeDate.valueOf());
    } else {
      this.activeDate = new Date();
    }
  }

  /**
   * This function handles changes from the Inputs
   *
   * @param changes changes from inputs
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.maxDate !== undefined) {
      if (DateService.isDate(changes.maxDate.currentValue)) {
        this.maxDate = changes.maxDate.currentValue;
        this.refreshView();
      }
    } else if (changes.minDate !== undefined) {
      if (DateService.isDate(changes.minDate.currentValue)) {
        this.minDate = changes.minDate.currentValue;
        this.refreshView();
      }
    } else if (changes.parsedDate !== undefined) {
      const date: Date = changes.parsedDate.currentValue;
      if (DateService.isDate(date)) {
        this.parsedDate = date;
        this.select(date, false);
      } else {
        this.selectedDate = undefined;
        this.refreshView();
      }
    } else if (changes.mode !== undefined) {
      this.refreshView();
    }
  }

  /**
   * This function sets compare handler
   *
   * @param comparator function for handling date comparison
   * @param comparatorType mode type
   */
  setCompareHandler(
    comparator: DateComparator,
    comparatorType: ComparatorType
  ): void {
    if (comparatorType === 'day') {
      this.compareHandlerDay = comparator;
    }
    if (comparatorType === 'month') {
      this.compareHandlerMonth = comparator;
    }
    if (comparatorType === 'year') {
      this.compareHandlerYear = comparator;
    }
    if (comparatorType === 'dual-month-day') {
      this.compareHandlerDay = comparator;
    }
  }

  /**
   * This function compares dates using compare handler
   *
   * @param date1 Date to compare
   * @param date2 Date to compare
   * @returns Result of comparison
   */
  compare(date1: Date, date2: Date): number | undefined {
    if ((date1 ?? null) === null || (date2 ?? null) === null) {
      return undefined;
    }
    if (this.mode === 'day' && this.compareHandlerDay !== undefined) {
      return this.compareHandlerDay(date1, date2);
    }
    if (this.mode === 'month' && this.compareHandlerMonth !== undefined) {
      return this.compareHandlerMonth(date1, date2);
    }
    if (this.mode === 'year' && this.compareHandlerYear !== undefined) {
      return this.compareHandlerYear(date1, date2);
    }
    if (this.mode === 'dual-month-day' && this.compareHandlerDay !== undefined) {
      return this.compareHandlerDay(date1, date2);
    }
    return undefined;
  }

  /**
   * This function sets refresh view handler
   *
   * @param handler function for displaying view
   * @param type mode type
   */
  setRefreshViewHandler(handler: () => void, type: string): void {
    if (type === 'day') {
      this.refreshViewHandlerDay = handler;
    } else if (type === 'month') {
      this.refreshViewHandlerMonth = handler;
    } else if (type === 'year') {
      this.refreshViewHandlerYear = handler;
    } else if (type === 'dual-month-day') {
      this.refreshViewHandlerDualMonthDay = handler;
    }
  }

  /**
   * This function calls the refresh view handler depending on the current mode
   */
  refreshView(): void {
    if (this.mode === 'day' && this.refreshViewHandlerDay !== undefined) {
      this.refreshViewHandlerDay();
    } else if (
      this.mode === 'month' &&
      this.refreshViewHandlerMonth !== undefined
    ) {
      this.refreshViewHandlerMonth();
    } else if (
      this.mode === 'year' &&
      this.refreshViewHandlerYear !== undefined
    ) {
      this.refreshViewHandlerYear();
    } else if (
      this.mode === 'dual-month-day' &&
      this.refreshViewHandlerDualMonthDay !== undefined
    ) {
      this.refreshViewHandlerDualMonthDay();
    }
  }

  /**
   * This function formats date
   *
   * @param date Date to format
   * @param format Date format
   * @returns Formatted date
   */
  formatDate(date: Date, format: DatePickerFormatType): string {
    return this.dateFormatterService.formatDate(date, format);
  }

  /**
   * This function checks if date is active
   *
   * @param dateObject Date to check for active
   * @returns Returns whether date is active
   */
  isActive(dateObject: DateObject): boolean {
    if (this.compare(dateObject.date, this.activeDate) === 0) {
      this.activeDateId.next(dateObject.uid);
      return true;
    }
    return false;
  }

  /**
   * This function converts Date to DateObject
   *
   * @param date Date to convert to DateObject
   * @param format Date format for label
   * @returns Returns converted DateObject.
   */
  createDateObject(date: Date, format: DatePickerFormatType): DateObject {
    const dateObject: DateObject = {} as unknown as DateObject;
    dateObject.date = new Date(date.valueOf());
    dateObject.label = this.formatDate(date, format);
    dateObject.selected = this.compare(date, this.selectedDate) === 0;
    dateObject.disabled = this.isDisabled(date);
    dateObject.current = this.compare(date, new Date()) === 0;
    return dateObject;
  }

  /**
   * Split array into smaller arrays
   *
   * @param arr Array to split
   * @param size Size of new array
   * @returns Returns splitted array.
   */
  split(arr: Array<unknown>, size: number): Array<unknown> {
    const arrays: Array<unknown> = [];
    while (arr.length > 0) {
      arrays.push(arr.splice(0, size));
    }
    return arrays;
  }

  /**
   * This function handles selecting a date
   *
   * @param date Date to select
   * @param isManual True if date has been selected manually
   */
  select(date: Date, isManual: boolean = true): void {
    if (this.mode === this.minMode || this.mode === 'dual-month-day') {
      if (this.activeDate === undefined || this.activeDate === null) {
        this.activeDate = new Date(0, 0, 0, 0, 0, 0, 0);
      }
      this.activeDate = new Date(date.valueOf());
      if (isManual) {
        this.selectionDone.emit(this.activeDate);
        if (this.datePicker !== null) {
          this.datePicker.focusInput();
        }
      }
    } else {
      this.activeDate = new Date(date.valueOf());
      if (isManual) {
        this.mode = this.modes[this.modes.indexOf(this.mode) - 1];
      }
      this.calendarFocusEventEmitter.emit(true);
    }
    this.selectedDate = new Date(this.activeDate.valueOf());
    this.refreshView();
  }

  /**
   * This function moves date to previous/next
   *
   * @param direction Determines whether it should move previous or next
   */
  move(direction: number): void {
    const expectedStep: StepType = this.step.get(this.mode);
    if (expectedStep) {
      const yearStep: number =
        expectedStep.years === undefined ? 0 : expectedStep.years;
      const monthStep: number =
        expectedStep.months === undefined ? 0 : expectedStep.months;
      const year: number = this.activeDate.getFullYear() + direction * yearStep;
      const month: number = this.activeDate.getMonth() + direction * monthStep;
      this.activeDate = new Date(year, month, 1);
      this.refreshView();
    }
  }

  /**
   * This function toggles date picker's view mode (day, month, year)
   *
   * @param _direction Determines which mode to display
   */
  toggleMode(_direction: number): void {

    const direction = _direction === 0 ? 1 : _direction;
    if (
      !(this.mode === this.maxMode && direction === 1) &&
      !(this.mode === this.minMode && direction === -1)
    ) {
      this.mode = this.modes[this.modes.indexOf(this.mode) + direction];
      this.refreshView();
      this.calendarFocusEventEmitter.emit(true);
    }
  }

  /**
   * This function returns whether a date is disabled
   *
   * @param date Date to check if it is disabled
   * @returns Returns true if the date is disabled.
   */
  protected isDisabled(date: Date): boolean {

    let disabled = false;
    if ((this.mode === 'day' || this.mode === 'dual-month-day') && this.disabledDates !== undefined) {
      disabled = this.disabledDates(date);
    }
    return (
      disabled ||
      this.compare(date, this.minDate) < 0 ||
      this.compare(date, this.maxDate) > 0
    );
  }
}
